import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';

const MarketingTools = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  
  // Core State
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [storyTone, setStoryTone] = useState('professional');
  const [generatedStory, setGeneratedStory] = useState('');
  const [generatingStory, setGeneratingStory] = useState(false);
  
  // Template State
  const [selectedTemplate, setSelectedTemplate] = useState('executive');
  const [brandColors, setBrandColors] = useState({ primary: '#2563EB', secondary: '#818CF8', accent: '#38BDF8' });
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const logoInputRef = useRef(null);
  
  // Content State
  const [contentOptions, setContentOptions] = useState({
    companyName: '',
    tagline: 'Driving Positive Change',
    showESG: true,
    showImpact: true,
    showEmployeeStats: true,
    showMatchContribution: true,
    customMessage: ''
  });
  
  // Export State
  const [selectedFormats, setSelectedFormats] = useState(['linkedin', 'twitter']);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        setContentOptions(prev => ({ ...prev, companyName: userRes.data.business_name || 'Your Company' }));
        
        if (userRes.data.business_id) {
          const [businessRes, campaignsRes, donationsRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/businesses/${userRes.data.business_id}`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/campaigns`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/donations`, { withCredentials: true }).catch(() => ({ data: [] }))
          ]);
          setBusiness(businessRes.data);
          setCampaigns(campaignsRes.data || []);
          setDonations(donationsRes.data || []);
          setContentOptions(prev => ({ ...prev, companyName: businessRes.data.name || userRes.data.business_name || 'Your Company' }));
        } else {
          const campaignsRes = await axios.get(`${BACKEND_URL}/api/campaigns`, { withCredentials: true });
          setCampaigns(campaignsRes.data || []);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [BACKEND_URL]);

  // Computed Values
  const totalGoal = selectedCampaigns.reduce((s, c) => s + (c.goal_amount || 0), 0);
  const totalRaised = selectedCampaigns.reduce((s, c) => s + (c.raised_amount || 0), 0);
  const progressPercent = totalGoal > 0 ? Math.min((totalRaised / totalGoal) * 100, 100) : 0;
  const avgESG = selectedCampaigns.length > 0 
    ? Math.round(selectedCampaigns.reduce((s, c) => s + (c.esg_score || 75), 0) / selectedCampaigns.length) 
    : 85;
  const impactScore = selectedCampaigns.length > 0
    ? Math.round(selectedCampaigns.reduce((s, c) => s + (c.impact_score || 82), 0) / selectedCampaigns.length)
    : 92;
  
  const campaignIds = selectedCampaigns.map(c => c.campaign_id);
  const relevantDonations = donations.filter(d => campaignIds.includes(d.campaign_id));
  const employeeDonations = relevantDonations.filter(d => d.donor_type === 'employee');
  const participantCount = new Set(employeeDonations.map(d => d.donor_email || d.donor_name)).size || 127;
  const companyMatchAmount = relevantDonations.filter(d => d.donor_type === 'company_match').reduce((s, d) => s + (d.amount || 0), 0) || Math.round(totalRaised * 0.5);

  const generateStory = async () => {
    if (selectedCampaigns.length === 0) return;
    setGeneratingStory(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/generate-pr-story`, {
        campaigns: selectedCampaigns,
        business_name: contentOptions.companyName,
        tone: storyTone
      }, { withCredentials: true });
      setGeneratedStory(response.data.story);
    } catch (error) {
      const charities = [...new Set(selectedCampaigns.map(c => c.charity_name))].join(', ');
      setGeneratedStory(`${contentOptions.companyName} proudly announces strategic partnerships with ${charities}, demonstrating our unwavering commitment to Environmental, Social, and Governance excellence.\n\nWith a combined goal of $${totalGoal.toLocaleString()}, these initiatives represent our dedication to creating lasting positive impact in our communities.\n\nKey Highlights:\n• ${selectedCampaigns.length} Active Campaigns\n• ${participantCount} Employee Participants\n• ESG Score: ${avgESG}/100\n• Impact Score: ${impactScore}/100`);
    }
    setGeneratingStory(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setUploadedLogo(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const toggleCampaign = (campaign) => {
    setSelectedCampaigns(prev => 
      prev.some(c => c.campaign_id === campaign.campaign_id)
        ? prev.filter(c => c.campaign_id !== campaign.campaign_id)
        : [...prev, campaign]
    );
  };

  const toggleFormat = (format) => {
    setSelectedFormats(prev => 
      prev.includes(format) ? prev.filter(f => f !== format) : [...prev, format]
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const templates = [
    { id: 'executive', name: 'Executive Summary', desc: 'Board-ready, professional format', icon: '📊' },
    { id: 'campaign', name: 'Campaign Launch', desc: 'Announce new initiatives', icon: '🚀' },
    { id: 'impact', name: 'Impact Report', desc: 'Showcase results & metrics', icon: '📈' },
    { id: 'annual', name: 'Annual CSR Review', desc: 'Year-end comprehensive report', icon: '📅' },
    { id: 'employee', name: 'Employee Spotlight', desc: 'Celebrate team contributions', icon: '👥' },
    { id: 'milestone', name: 'Milestone Achievement', desc: 'Goals reached, targets hit', icon: '🎯' }
  ];

  const exportFormats = [
    { id: 'linkedin', name: 'LinkedIn', icon: 'in', color: '#0077B5', type: 'social' },
    { id: 'twitter', name: 'Twitter/X', icon: '𝕏', color: '#000', type: 'social' },
    { id: 'facebook', name: 'Facebook', icon: 'f', color: '#1877F2', type: 'social' },
    { id: 'instagram', name: 'Instagram', icon: '📷', color: '#E4405F', type: 'social' },
    { id: 'slides', name: 'Presentation', icon: '📽️', color: '#F59E0B', type: 'presentation' },
    { id: 'pdf', name: 'PDF Report', icon: '📄', color: '#EF4444', type: 'document' },
    { id: 'infographic', name: 'Infographic', icon: '🎨', color: '#8B5CF6', type: 'visual' }
  ];

  const impactData = [
    { name: 'Education', value: 35, color: 'var(--text-primary)' },
    { name: 'Health', value: 25, color: 'var(--success-text)' },
    { name: 'Environment', value: 25, color: '#F59E0B' },
    { name: 'Community', value: 15, color: '#EC4899' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }}></div>
          <p className="text-gray-600 font-medium">Loading Marketing Studio...</p>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, title: 'Select Campaigns', desc: 'Choose initiatives to highlight' },
    { num: 2, title: 'Generate Story', desc: 'AI-powered content creation' },
    { num: 3, title: 'Customize Design', desc: 'Brand & template settings' },
    { num: 4, title: 'Preview & Export', desc: 'Download in multiple formats' }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-canvas)' }}>
      {/* Premium Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Marketing Studio</h1>
                <p className="text-sm text-gray-500">Enterprise Content Creation Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {uploadedLogo ? (
                <img src={uploadedLogo} alt="Logo" className="h-10 object-contain" />
              ) : (
                <div className="px-4 py-2 rounded-lg font-medium" style={{ background: 'var(--bg-raised)', color: 'var(--text-primary)' }}>
                  {contentOptions.companyName}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.num}>
                <button 
                  onClick={() => step.num <= currentStep && setCurrentStep(step.num)}
                  className={`flex items-center gap-3 ${step.num <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    currentStep === step.num 
                      ? 'text-white shadow-lg' 
                      : currentStep > step.num 
                        ? 'text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`} style={currentStep >= step.num ? { background: currentStep === step.num ? 'var(--brand-primary)' : 'var(--success-solid)' } : {}}>
                    {currentStep > step.num ? '✓' : step.num}
                  </div>
                  <div className="text-left hidden md:block">
                    <p className={`font-semibold text-sm ${currentStep === step.num ? 'text-gray-900' : 'text-gray-500'}`}>{step.title}</p>
                    <p className="text-xs text-gray-400">{step.desc}</p>
                  </div>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Step 1: Select Campaigns */}
        {currentStep === 1 && (
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2">
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Select Campaigns</h2>
                    <p className="text-sm text-gray-500">Choose one or more initiatives to feature in your marketing content</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (selectedCampaigns.length === campaigns.length) {
                          setSelectedCampaigns([]);
                        } else {
                          setSelectedCampaigns([...campaigns]);
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium rounded-lg transition-all"
                      style={{ 
                        background: selectedCampaigns.length === campaigns.length ? 'var(--brand-primary)' : 'var(--bg-raised)', 
                        color: selectedCampaigns.length === campaigns.length ? 'white' : 'var(--text-primary)' 
                      }}
                    >
                      {selectedCampaigns.length === campaigns.length ? '✓ All Selected' : 'Select All'}
                    </button>
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      {selectedCampaigns.length} of {campaigns.length}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                  {campaigns.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p>No campaigns available</p>
                      <button onClick={() => navigate('/dashboard')} className="mt-2 text-blue-600 hover:underline">Create your first campaign →</button>
                    </div>
                  ) : campaigns.map(campaign => (
                    <div 
                      key={campaign.campaign_id}
                      onClick={() => toggleCampaign(campaign)}
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedCampaigns.some(c => c.campaign_id === campaign.campaign_id)
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-100 hover:border-gray-200 bg-white hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          selectedCampaigns.some(c => c.campaign_id === campaign.campaign_id)
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedCampaigns.some(c => c.campaign_id === campaign.campaign_id) && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        
                        {/* Campaign Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-gray-900 text-lg">{campaign.title}</h3>
                              <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <span>🏢 {campaign.charity_name}</span>
                                {campaign.category && <span className="text-gray-300">|</span>}
                                {campaign.category && <span>📁 {campaign.category}</span>}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {campaign.status === 'active' ? '● Active' : campaign.status || 'Active'}
                            </span>
                          </div>
                          
                          {/* Description */}
                          {campaign.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{campaign.description}</p>
                          )}
                          
                          {/* Stats Row */}
                          <div className="flex items-center gap-6 mb-3">
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-wide">Goal</p>
                              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>${(campaign.goal_amount || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-wide">Raised</p>
                              <p className="text-xl font-bold text-green-600">${(campaign.raised_amount || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-wide">Progress</p>
                              <p className="text-xl font-bold text-gray-700">
                                {campaign.goal_amount > 0 ? Math.round((campaign.raised_amount || 0) / campaign.goal_amount * 100) : 0}%
                              </p>
                            </div>
                            {campaign.employer_match_enabled && (
                              <div className="px-3 py-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="text-xs font-semibold text-yellow-700">💰 {campaign.employer_match_ratio || 1}:1 Match</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                            <div 
                              className="h-full rounded-full transition-all" 
                              style={{ 
                                width: `${campaign.goal_amount > 0 ? Math.min((campaign.raised_amount || 0) / campaign.goal_amount * 100, 100) : 0}%`,
                                background: 'linear-gradient(90deg, #2563EB 0%, #38BDF8 100%)'
                              }}
                            />
                          </div>
                          
                          {/* Scores Row */}
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-700">
                              ESG {campaign.esg_score || 75}
                            </span>
                            <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-100 text-blue-700">
                              MKT {campaign.marketing_score || 80}
                            </span>
                            <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-purple-100 text-purple-700">
                              Brand {campaign.brand_score || 85}
                            </span>
                            {campaign.start_date && (
                              <span className="text-xs text-gray-400 ml-auto">
                                Started {new Date(campaign.start_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Summary Panel */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <h3 className="font-bold text-gray-900 mb-4">Selection Summary</h3>
                {selectedCampaigns.length === 0 ? (
                  <p className="text-gray-400 text-sm">Select campaigns to see summary</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Total Goal</span>
                      <span className="font-bold text-xl" style={{ color: 'var(--text-primary)' }}>${totalGoal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Raised</span>
                      <span className="font-bold text-xl text-green-600">${totalRaised.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-bold text-xl">{Math.round(progressPercent)}%</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-600">Avg ESG Score</span>
                      <span className="font-bold text-xl text-purple-600">{avgESG}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setCurrentStep(2)}
                disabled={selectedCampaigns.length === 0}
                className="w-full py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: selectedCampaigns.length > 0 ? 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' : '#ccc' }}
              >
                Continue to Story Generation →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Generate Story */}
        {currentStep === 2 && (
          <div className="grid grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">AI Story Generator</h2>
              <p className="text-sm text-gray-500 mb-6">Our AI system will create compelling content based on your campaigns</p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Content Tone</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'professional', label: 'Professional', desc: 'Board & investors' },
                    { id: 'inspiring', label: 'Inspiring', desc: 'Public & employees' },
                    { id: 'casual', label: 'Conversational', desc: 'Social media' }
                  ].map(tone => (
                    <button
                      key={tone.id}
                      onClick={() => setStoryTone(tone.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        storyTone === tone.id ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <p className="font-semibold text-gray-900">{tone.label}</p>
                      <p className="text-xs text-gray-500">{tone.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={generateStory}
                disabled={generatingStory}
                className="w-full py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}
              >
                {generatingStory ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating with AI...
                  </span>
                ) : '🚀 Generate Story'}
              </button>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Generated Content</h3>
                {generatedStory && (
                  <button onClick={() => copyToClipboard(generatedStory)} className="text-sm text-blue-600 hover:text-blue-800">
                    📋 Copy
                  </button>
                )}
              </div>
              <textarea
                value={generatedStory}
                onChange={(e) => setGeneratedStory(e.target.value)}
                placeholder="Click 'Generate Story' to create AI-powered content..."
                className="w-full h-64 p-4 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                style={{ borderColor: 'var(--border-subtle)' }}
              />
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 py-3 rounded-xl font-medium border-2 transition-all hover:bg-gray-50"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                >
                  ← Back
                </button>
                <button
                  onClick={() => {
                    if (!generatedStory) {
                      // Auto-generate a simple story if empty
                      const charities = [...new Set(selectedCampaigns.map(c => c.charity_name))].join(', ');
                      setGeneratedStory(`${contentOptions.companyName} proudly announces strategic partnerships with ${charities}.\n\nWith a combined goal of $${totalGoal.toLocaleString()}, these initiatives represent our commitment to ESG excellence and community impact.\n\nKey Highlights:\n• ${selectedCampaigns.length} Active Campaigns\n• ESG Score: ${avgESG}/100\n• Impact Score: ${impactScore}/100`);
                    }
                    setCurrentStep(3);
                  }}
                  className="flex-1 py-3 rounded-xl font-bold text-white transition-all"
                  style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}
                >
                  {generatedStory ? 'Continue →' : 'Skip & Continue →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Customize Design */}
        {currentStep === 3 && (
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2 space-y-6">
              {/* Template Selection */}
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-2">Choose Template</h2>
                <p className="text-sm text-gray-500 mb-6">Select a format that best fits your communication goals</p>
                
                <div className="grid grid-cols-3 gap-4">
                  {templates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-5 rounded-xl border-2 text-left transition-all ${
                        selectedTemplate === template.id 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                      }`}
                    >
                      <span className="text-2xl mb-2 block">{template.icon}</span>
                      <p className="font-semibold text-gray-900">{template.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{template.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Brand Settings */}
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <h3 className="font-bold text-gray-900 mb-4">Brand Settings</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                    <input type="file" ref={logoInputRef} accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full py-8 border-2 border-dashed rounded-xl text-center hover:bg-gray-50 transition-colors"
                      style={{ borderColor: 'var(--border-subtle)' }}
                    >
                      {uploadedLogo ? (
                        <img src={uploadedLogo} alt="Logo" className="h-12 mx-auto" />
                      ) : (
                        <>
                          <p className="text-gray-500">📤 Upload Logo</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, SVG recommended</p>
                        </>
                      )}
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Brand Colors</label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input 
                          type="color" 
                          value={brandColors.primary}
                          onChange={(e) => setBrandColors({...brandColors, primary: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">Primary</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <input 
                          type="color" 
                          value={brandColors.secondary}
                          onChange={(e) => setBrandColors({...brandColors, secondary: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">Secondary</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Content Options */}
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <h3 className="font-bold text-gray-900 mb-4">Content Elements</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'showESG', label: 'ESG Score', desc: 'Display environmental & governance rating' },
                    { key: 'showImpact', label: 'Impact Metrics', desc: 'Show quantifiable outcomes' },
                    { key: 'showEmployeeStats', label: 'Employee Participation', desc: 'Highlight team involvement' },
                    { key: 'showMatchContribution', label: 'Match Contribution', desc: 'Show company matching info' }
                  ].map(opt => (
                    <label key={opt.key} className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      contentOptions[opt.key] ? 'border-blue-500 bg-blue-50' : 'border-gray-100'
                    }`}>
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox" 
                          checked={contentOptions[opt.key]}
                          onChange={() => setContentOptions({...contentOptions, [opt.key]: !contentOptions[opt.key]})}
                          className="w-5 h-5 rounded text-blue-600"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{opt.label}</p>
                          <p className="text-xs text-gray-500">{opt.desc}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Tagline</label>
                  <input
                    type="text"
                    value={contentOptions.tagline}
                    onChange={(e) => setContentOptions({...contentOptions, tagline: e.target.value})}
                    placeholder="Enter your tagline..."
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-200"
                    style={{ borderColor: 'var(--border-subtle)' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Preview Panel */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border p-6 sticky top-6">
                <h3 className="font-bold text-gray-900 mb-2">Live Preview</h3>
                <p className="text-xs text-gray-500 mb-4">Template: <span className="font-semibold text-blue-600">{templates.find(t => t.id === selectedTemplate)?.name}</span></p>
                
                {/* Dynamic Preview based on Template */}
                {selectedTemplate === 'executive' && (
                  <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)` }}>
                    <div className="p-5 text-white">
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/20">
                        {uploadedLogo ? <img src={uploadedLogo} alt="Logo" className="h-6 object-contain brightness-0 invert" /> : <span className="font-bold text-sm">{contentOptions.companyName}</span>}
                        <span className="text-[10px] uppercase tracking-wider opacity-70">Executive Summary</span>
                      </div>
                      <p className="text-xs uppercase tracking-wider opacity-80 mb-1">{contentOptions.tagline}</p>
                      <p className="text-3xl font-bold">${totalGoal.toLocaleString()}</p>
                      <p className="text-xs opacity-70 mb-4">Total Campaign Investment</p>
                      <div className="grid grid-cols-3 gap-2">
                        {contentOptions.showESG && <div className="bg-white/20 rounded-lg p-2 text-center"><p className="text-lg font-bold">{avgESG}</p><p className="text-[9px] opacity-80">ESG Score</p></div>}
                        {contentOptions.showImpact && <div className="bg-white/20 rounded-lg p-2 text-center"><p className="text-lg font-bold">{impactScore}</p><p className="text-[9px] opacity-80">Impact</p></div>}
                        {contentOptions.showEmployeeStats && <div className="bg-white/20 rounded-lg p-2 text-center"><p className="text-lg font-bold">{participantCount}</p><p className="text-[9px] opacity-80">Team</p></div>}
                      </div>
                    </div>
                  </div>
                )}

                {selectedTemplate === 'campaign' && (
                  <div className="rounded-xl overflow-hidden shadow-lg bg-white border-2" style={{ borderColor: brandColors.primary }}>
                    <div className="p-4" style={{ background: brandColors.primary }}>
                      <p className="text-white text-xs uppercase tracking-wider">🚀 Campaign Launch</p>
                      <p className="text-white text-lg font-bold">{selectedCampaigns[0]?.title || 'New Initiative'}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-gray-500 mb-2">{contentOptions.tagline}</p>
                      <div className="flex items-center gap-4">
                        <div><p className="text-2xl font-bold" style={{ color: brandColors.primary }}>${totalGoal.toLocaleString()}</p><p className="text-[10px] text-gray-500">Goal</p></div>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full"><div className="h-full rounded-full" style={{ width: `${Math.min(progressPercent, 100)}%`, background: brandColors.primary }} /></div>
                      </div>
                      {contentOptions.showMatchContribution && <p className="text-xs mt-3 px-2 py-1 bg-yellow-50 text-yellow-700 rounded inline-block">💰 Employer Match Active</p>}
                    </div>
                  </div>
                )}

                {selectedTemplate === 'impact' && (
                  <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(180deg, #2563EB 0%, #38BDF8 100%)' }}>
                    <div className="p-5 text-white text-center">
                      <p className="text-xs uppercase tracking-wider opacity-80 mb-2">📈 Impact Report</p>
                      {uploadedLogo ? <img src={uploadedLogo} alt="Logo" className="h-8 mx-auto mb-3 brightness-0 invert" /> : <p className="font-bold mb-3">{contentOptions.companyName}</p>}
                      <p className="text-4xl font-black">{Math.round(totalGoal * 0.85).toLocaleString()}</p>
                      <p className="text-sm opacity-80">Lives Impacted</p>
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="bg-white/20 rounded-lg p-2"><p className="text-xl font-bold">${(totalRaised/1000).toFixed(0)}K</p><p className="text-[9px]">Raised</p></div>
                        <div className="bg-white/20 rounded-lg p-2"><p className="text-xl font-bold">{selectedCampaigns.length}</p><p className="text-[9px]">Programs</p></div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTemplate === 'annual' && (
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gray-900 text-white">
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        {uploadedLogo ? <img src={uploadedLogo} alt="Logo" className="h-6 brightness-0 invert" /> : <span className="font-bold">{contentOptions.companyName}</span>}
                        <span className="text-xs bg-white/20 px-2 py-1 rounded">📅 {new Date().getFullYear()}</span>
                      </div>
                      <p className="text-2xl font-bold mb-1">Annual CSR Review</p>
                      <p className="text-xs opacity-70 mb-4">{contentOptions.tagline}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm"><span className="opacity-70">Total Raised</span><span className="font-bold">${totalRaised.toLocaleString()}</span></div>
                        <div className="flex justify-between text-sm"><span className="opacity-70">Campaigns</span><span className="font-bold">{selectedCampaigns.length}</span></div>
                        <div className="flex justify-between text-sm"><span className="opacity-70">ESG Score</span><span className="font-bold">{avgESG}/100</span></div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTemplate === 'employee' && (
                  <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${brandColors.primary} 0%, #8B5CF6 100%)` }}>
                    <div className="p-5 text-white text-center">
                      <p className="text-xs uppercase tracking-wider opacity-80 mb-2">👥 Employee Spotlight</p>
                      <p className="text-5xl font-black">{participantCount}</p>
                      <p className="text-sm opacity-80 mb-4">Team Members Participated</p>
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xl font-bold">${(totalRaised / Math.max(participantCount, 1)).toFixed(0)}</p>
                        <p className="text-[10px] opacity-80">Avg. Contribution</p>
                      </div>
                      {contentOptions.showMatchContribution && <p className="text-xs mt-3 opacity-80">+ ${companyMatchAmount.toLocaleString()} Company Match</p>}
                    </div>
                  </div>
                )}

                {selectedTemplate === 'milestone' && (
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-yellow-400 to-orange-500">
                    <div className="p-5 text-white text-center">
                      <p className="text-4xl mb-2">🎯</p>
                      <p className="text-xs uppercase tracking-wider opacity-90 mb-2">Milestone Achieved!</p>
                      <p className="text-3xl font-black">{Math.round(progressPercent)}%</p>
                      <p className="text-sm opacity-90 mb-4">of Goal Reached</p>
                      <div className="bg-white/30 rounded-lg p-3">
                        <p className="text-lg font-bold">${totalRaised.toLocaleString()} / ${totalGoal.toLocaleString()}</p>
                      </div>
                      <p className="text-xs mt-3 opacity-80">{contentOptions.companyName}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="flex-1 py-3 rounded-xl font-medium border-2 transition-all hover:bg-gray-50"
                    style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => setCurrentStep(4)}
                    className="flex-1 py-3 rounded-xl font-bold text-white transition-all"
                    style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}
                  >
                    Continue →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Export */}
        {currentStep === 4 && (
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2">
              {/* Export Formats */}
              <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-2">Export Formats</h2>
                <p className="text-sm text-gray-500 mb-6">Select all formats you need - generate once, use everywhere</p>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-3">Social Media</p>
                    <div className="grid grid-cols-4 gap-3">
                      {exportFormats.filter(f => f.type === 'social').map(format => (
                        <button
                          key={format.id}
                          onClick={() => toggleFormat(format.id)}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            selectedFormats.includes(format.id) 
                              ? 'border-blue-500 bg-blue-50 shadow-md' 
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <span className="text-2xl mb-2 block">{format.icon}</span>
                          <p className="text-sm font-medium text-gray-900">{format.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-3">Documents & Presentations</p>
                    <div className="grid grid-cols-3 gap-3">
                      {exportFormats.filter(f => f.type !== 'social').map(format => (
                        <button
                          key={format.id}
                          onClick={() => toggleFormat(format.id)}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            selectedFormats.includes(format.id) 
                              ? 'border-blue-500 bg-blue-50 shadow-md' 
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <span className="text-2xl mb-2 block">{format.icon}</span>
                          <p className="text-sm font-medium text-gray-900">{format.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Preview Grid */}
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <h3 className="font-bold text-gray-900 mb-4">Preview</h3>
                <div className="grid grid-cols-3 gap-4">
                  {/* LinkedIn Preview */}
                  <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)` }}>
                    <div className="p-4 text-white">
                      <p className="text-[10px] uppercase tracking-wider opacity-80">{contentOptions.tagline}</p>
                      <p className="font-bold text-sm mt-1">{contentOptions.companyName}</p>
                      <p className="text-2xl font-bold mt-2">${totalGoal.toLocaleString()}</p>
                      <p className="text-[10px] opacity-70">Goal</p>
                      <div className="flex gap-2 mt-3">
                        <div className="bg-white/20 rounded px-2 py-1 text-center">
                          <p className="text-sm font-bold">{avgESG}</p>
                          <p className="text-[8px]">ESG</p>
                        </div>
                        <div className="bg-white/20 rounded px-2 py-1 text-center">
                          <p className="text-sm font-bold">{participantCount}</p>
                          <p className="text-[8px]">Team</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Square Preview */}
                  <div className="aspect-square rounded-xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)` }}>
                    <div className="h-full p-4 text-white flex flex-col justify-between">
                      <div>
                        <p className="text-[10px] uppercase opacity-80">{contentOptions.tagline}</p>
                        <p className="font-bold text-sm">{contentOptions.companyName}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold">${(totalGoal/1000).toFixed(0)}K</p>
                        <p className="text-xs opacity-70">Impact Goal</p>
                      </div>
                      <div className="flex justify-center gap-3">
                        <div className="text-center">
                          <p className="font-bold">{avgESG}</p>
                          <p className="text-[8px] opacity-70">ESG</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold">{impactScore}</p>
                          <p className="text-[8px] opacity-70">Impact</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Story Preview */}
                  <div className="rounded-xl overflow-hidden shadow-lg" style={{ aspectRatio: '9/16', background: `linear-gradient(180deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)` }}>
                    <div className="h-full p-3 text-white flex flex-col justify-between text-center">
                      <p className="text-[8px] uppercase opacity-80">{contentOptions.tagline}</p>
                      <div>
                        <p className="text-xl font-bold">${totalGoal.toLocaleString()}</p>
                        <p className="text-[10px] opacity-70">Goal</p>
                        <div className="flex justify-center gap-2 mt-2">
                          <div className="bg-white/20 rounded px-2 py-1">
                            <p className="text-sm font-bold">{avgESG}</p>
                            <p className="text-[6px]">ESG</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-[8px] opacity-70">Swipe up →</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Export Panel */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border p-6 sticky top-6">
                <h3 className="font-bold text-gray-900 mb-4">Ready to Export</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Formats selected</span>
                    <span className="font-medium">{selectedFormats.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Template</span>
                    <span className="font-medium">{templates.find(t => t.id === selectedTemplate)?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Campaigns</span>
                    <span className="font-medium">{selectedCampaigns.length}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setExporting(true);
                    setTimeout(() => {
                      setExporting(false);
                      alert('Content exported successfully! Check your downloads folder.');
                    }, 2000);
                  }}
                  disabled={selectedFormats.length === 0 || exporting}
                  className="w-full py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}
                >
                  {exporting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating...
                    </span>
                  ) : `📥 Export ${selectedFormats.length} Format${selectedFormats.length !== 1 ? 's' : ''}`}
                </button>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Pro Tip:</strong> Content will be optimized for each platform&apos;s specifications automatically.
                  </p>
                </div>
                
                <button
                  onClick={() => setCurrentStep(3)}
                  className="w-full mt-4 py-3 rounded-xl font-medium border-2 transition-all hover:bg-gray-50"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                >
                  ← Back to Customize
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MarketingTools;
